import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchBlogPosts, fetchBlogCategories, type BlogPost } from '@/services/api';

const PAGE_SIZE = 6;

export default function BlogPage() {
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<string[]>(['All']);
  const [activeCategory, setActiveCategory] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  // 加载分类
  useEffect(() => {
    fetchBlogCategories()
      .then((cats) => {
        const names = ['All', ...cats.map((c) => c.name)];
        setCategories(names);
      })
      .catch(() => {});
  }, []);

  // 加载文章
  useEffect(() => {
    setLoading(true);
    fetchBlogPosts(currentPage, PAGE_SIZE, activeCategory)
      .then((res) => {
        setBlogPosts(res.items);
        setTotalPages(res.total_pages);
      })
      .catch(() => {
        setBlogPosts([]);
        setTotalPages(1);
      })
      .finally(() => setLoading(false));
  }, [currentPage, activeCategory]);

  useEffect(() => {
    document.title = 'News Center | YUNXIN OUTDOOR — Industry Insights & Updates';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', 'Stay updated with Yunxin Outdoor news — tool bag industry insights, product guides, manufacturing updates, and company news from our team in Fujian, China.');
  }, []);

  const handleCategoryChange = (cat: string) => {
    setActiveCategory(cat);
    setCurrentPage(1);
  };

  const featured = blogPosts[0];
  const restPosts = featured ? blogPosts.slice(1) : [];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-stone-900">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <span className="text-amber-400 text-xs font-semibold tracking-widest uppercase mb-4 block">News Center</span>
          <h1 className="text-5xl md:text-6xl font-bold text-white leading-tight mb-4">
            News &amp; Insights
          </h1>
          <p className="text-stone-400 text-base max-w-xl leading-relaxed">
            Industry news, product guides, and company updates from the Yunxin team.
          </p>
        </div>
      </section>

      {/* Featured Post */}
      {!loading && featured && (
        <section className="py-16 border-b border-stone-100">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <p className="text-amber-600 text-xs font-semibold tracking-widest uppercase mb-6">Featured Article</p>
            <Link
              to={`/blog/${featured.id}`}
              className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center group cursor-pointer"
            >
              <div className="rounded-2xl overflow-hidden h-80 w-full">
                {featured.image && (
                  <img
                    src={featured.image}
                    alt={featured.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                )}
              </div>
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="bg-amber-100 text-amber-700 text-xs font-semibold px-3 py-1 rounded-full">{featured.category}</span>
                  <span className="text-stone-400 text-xs">{(featured.published_at && featured.published_at.startsWith('20') ? featured.published_at : featured.created_at)?.slice(0, 10)}</span>
                  <span className="text-stone-400 text-xs">{featured.read_time}</span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-stone-900 leading-tight mb-4 group-hover:text-amber-700 transition-colors">
                  {featured.title}
                </h2>
                <p className="text-stone-500 text-base leading-relaxed mb-6">{featured.excerpt}</p>
                <span className="inline-flex items-center gap-2 text-stone-900 text-sm font-semibold tracking-wider uppercase border-b border-stone-900 pb-0.5 group-hover:text-amber-700 group-hover:border-amber-700 transition-colors whitespace-nowrap">
                  Read Full Article
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line" />
                  </div>
                </span>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Category Filter + Article List */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          {/* Category Tabs */}
          <div className="flex gap-2 flex-wrap mb-10">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => handleCategoryChange(cat)}
                className={`px-5 py-2 rounded-full text-xs font-semibold tracking-wider uppercase transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeCategory === cat
                    ? 'bg-stone-900 text-white'
                    : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-20">
              <p className="text-stone-400">Loading articles...</p>
            </div>
          ) : blogPosts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-stone-400">No articles found.</p>
            </div>
          ) : (
            <>
              {/* Articles Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                {(featured ? restPosts : blogPosts).map((post) => (
                  <Link
                    key={post.id}
                    to={`/blog/${post.id}`}
                    className="group cursor-pointer block"
                  >
                    <div className="rounded-2xl overflow-hidden h-52 w-full mb-5">
                      {post.image && (
                        <img
                          src={post.image}
                          alt={post.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                        />
                      )}
                    </div>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="bg-amber-100 text-amber-700 text-xs font-medium px-2.5 py-0.5 rounded-full">{post.category}</span>
                      <span className="text-stone-400 text-xs">{(post.published_at && post.published_at.startsWith('20') ? post.published_at : post.created_at)?.slice(0, 10)}</span>
                    </div>
                    <h3 className="text-stone-900 font-bold text-base leading-snug mb-2 group-hover:text-amber-700 transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-stone-500 text-sm leading-relaxed line-clamp-2 mb-4">{post.excerpt}</p>
                    <span className="inline-flex items-center gap-1.5 text-stone-700 text-xs font-semibold tracking-wider uppercase group-hover:text-amber-600 transition-colors whitespace-nowrap">
                      Read More
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-arrow-right-line" />
                      </div>
                    </span>
                  </Link>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="w-10 h-10 flex items-center justify-center rounded-full border border-stone-200 text-stone-600 hover:border-stone-400 hover:text-stone-900 disabled:opacity-30 disabled:cursor-not-allowed transition-all cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line" />
                  </button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-10 h-10 flex items-center justify-center rounded-full text-sm font-semibold transition-all cursor-pointer ${
                        currentPage === page
                          ? 'bg-stone-900 text-white'
                          : 'border border-stone-200 text-stone-600 hover:border-stone-400 hover:text-stone-900'
                      }`}
                    >
                      {page}
                    </button>
                  ))}

                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="w-10 h-10 flex items-center justify-center rounded-full border border-stone-200 text-stone-600 hover:border-stone-400 hover:text-stone-900 disabled:opacity-30 disabled:cursor-not-allowed transition-all cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
